/**
 * One row in the Submissions list: photo thumbnail, names, category,
 * dishes, submitter email, "Approve" + "Reject" buttons. Clicking the
 * card body navigates to the detail page; the buttons stop propagation.
 */
import { Link } from 'react-router-dom';

import type { AdminSubmission } from '../lib/api';

export default function SubmissionCard({
  submission,
  busy,
  onApprove,
  onReject,
}: {
  submission: AdminSubmission;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const dishes = submission.dishes.length > 0 ? submission.dishes.join(' · ') : '—';

  return (
    <article className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:flex-row">
      <Link
        to={`/submissions/${submission.id}`}
        className="block shrink-0 sm:w-32"
        aria-label={`Open detail for ${submission.name_th}`}
      >
        {submission.photo_url.length > 0 ? (
          <img
            src={submission.photo_url}
            alt={submission.name_en ?? submission.name_th}
            className="h-32 w-full rounded-md object-cover sm:w-32"
            loading="lazy"
          />
        ) : (
          <div className="flex h-32 w-full items-center justify-center rounded-md bg-slate-100 text-xs text-slate-400 sm:w-32">
            No photo
          </div>
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <Link
          to={`/submissions/${submission.id}`}
          className="block focus:outline-none focus:ring-2 focus:ring-soi-500"
        >
          <h2 className="truncate text-base font-semibold text-slate-900">
            {submission.name_th}
          </h2>
          {submission.name_en !== null && submission.name_en !== undefined && submission.name_en.length > 0 && (
            <p className="truncate text-sm text-slate-600">{submission.name_en}</p>
          )}
        </Link>
        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
          <div>
            <dt className="inline font-medium text-slate-500">Category: </dt>
            <dd className="inline">{submission.category ?? '—'}</dd>
          </div>
          <div className="min-w-0 max-w-full truncate">
            <dt className="inline font-medium text-slate-500">Dishes: </dt>
            <dd className="inline">{dishes}</dd>
          </div>
          <div className="truncate">
            <dt className="inline font-medium text-slate-500">Submitter: </dt>
            <dd className="inline">{submission.submitter_email || '(anonymous)'}</dd>
          </div>
        </dl>
        {submission.suspicious_flags.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {submission.suspicious_flags.map((flag) => (
              <li
                key={flag}
                className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
              >
                {flag}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onApprove}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Approve
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onReject}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Reject
          </button>
        </div>
      </div>
    </article>
  );
}
